/**
 * FutureModulesPreview — Phase E compact preview of 4 specialised modules:
 *   13. Pediatric Oral Health Intelligence
 *   14. Orthodontic Intelligence
 *   15. Implant Health Intelligence
 *   16. Precision Oral Microbiome Intelligence
 *
 * Each renders as a single compact card with: scope, 3 educational "what
 * matters" bullets, and an "expanding soon" badge. Strictly educational —
 * NO disease prediction / NO diagnostic claims.
 */
import React from "react";
import { Baby, Brackets, Anchor, Microscope } from "lucide-react";

const CONTENT_BG = {
  pediatric: {
    title: "Педиатрично орално здраве",
    scope: "Малки деца (0-5 г.), деца в училищна възраст (6-12 г.) и тийнейджъри.",
    bullets: [
      "Времеви график на пробив на млечни/постоянни зъби — какво да очаквате на всяка възраст",
      "Кариес-риск в зависимост от хранене (бутилка със сладко през нощта, чести закуски)",
      "Точна доза флуоридна паста по възрастова група (smear до 2 г. → грахово зърно 3-6 г.)",
    ],
    note: "Превенция в детска възраст е най-силният единичен лост за орално здраве през целия живот.",
  },
  orthodontic: {
    title: "Ортодонтия (образователно)",
    scope: "Подравняване, оклузия, ортодонтско лечение и неговите ефекти.",
    bullets: [
      "Защо неправилното подравняване увеличава кариес-риска (трудно почистване)",
      "Хигиена с брекети и алайнери — обърнато внимание на интердентално миене",
      "Връзка между захапка, ВЧГ (бруксизъм) и стрес — sleep + mindfulness като лостове",
    ],
    note: "Не диагностицираме малоклузия. Винаги се консултирайте с ортодонт за клинична оценка.",
  },
  implant: {
    title: "Здраве на импланта",
    scope: "Превенция на пери-имплантит и поддръжка на остеоинтеграция.",
    bullets: [
      "Биологична ширина и значението на здравата мукоза около импланта",
      "Пушене + диабет — двата най-силни модифицируеми риска за пери-имплантит",
      "Дневна хигиена (специални четки, водни флосове) + редовни проф. поддръжки",
    ],
    note: "За хора с импланти оптимизирането на омега-3, vit D и контрола на HbA1c удължава живота на импланта.",
  },
  microbiome: {
    title: "Прецизен орален микробиом",
    scope: "Развиваща се област — щам-ниво разнообразие, биофилм карта.",
    bullets: [
      "Защитни видове (S. salivarius K12, L. reuteri) → пробиотични стратегии",
      "Кариогенни щамове (S. mutans, Lactobacilli) — намалени от диета + xylitol",
      "Пародонтопатогени (P. gingivalis, T. forsythia) — асоциирани с инфламация",
    ],
    note: "Бъдещ модул: персонална микробиомна карта от слюнна проба (когато потребителски тестове станат достъпни в България).",
  },
};

const CONTENT_EN = {
  pediatric: {
    title: "Pediatric oral health",
    scope: "Toddlers (0-5y), school-age (6-12y), teens.",
    bullets: [
      "Eruption timeline for primary & permanent teeth — what to expect at each age",
      "Caries risk by feeding pattern (bottle-at-bed, frequent snacking)",
      "Age-appropriate fluoride dosing (smear ≤2y → rice-grain → pea-sized)",
    ],
    note: "Childhood prevention is the single strongest lever for life-long oral health.",
  },
  orthodontic: {
    title: "Orthodontic (educational)",
    scope: "Alignment, occlusion, orthodontic treatment and its effects.",
    bullets: [
      "Why misalignment raises caries risk (harder to clean)",
      "Hygiene with braces and aligners — interdental brushing matters",
      "Bite, TMJ/bruxism, stress — sleep + mindfulness as levers",
    ],
    note: "We don't diagnose malocclusion. Always consult an orthodontist for clinical assessment.",
  },
  implant: {
    title: "Implant health",
    scope: "Peri-implantitis prevention and osseointegration maintenance.",
    bullets: [
      "Biologic width and healthy peri-implant mucosa",
      "Smoking + diabetes — strongest modifiable peri-implantitis risks",
      "Daily hygiene (interdental brushes, water flosser) + regular maintenance",
    ],
    note: "For implant patients, omega-3, Vit D, and HbA1c control prolong implant lifespan.",
  },
  microbiome: {
    title: "Precision oral microbiome",
    scope: "Emerging field — strain-level diversity, biofilm mapping.",
    bullets: [
      "Protective strains (S. salivarius K12, L. reuteri) → probiotic strategies",
      "Cariogenic strains (S. mutans, Lactobacilli) — reduced by diet + xylitol",
      "Periopathogens (P. gingivalis, T. forsythia) — linked to inflammation",
    ],
    note: "Future: personal saliva-based microbiome map (when consumer tests become available in Bulgaria).",
  },
};

const MODULES = [
  { key: "pediatric",   icon: Baby,       color: "#F97316" },
  { key: "orthodontic", icon: Brackets,   color: "#A855F7" },
  { key: "implant",     icon: Anchor,     color: "#0EA5E9" },
  { key: "microbiome",  icon: Microscope, color: "#10B981" },
];

export default function FutureModulesPreview({ isDark, lang = "bg", cardCls }) {
  const bg = lang === "bg";
  const content = bg ? CONTENT_BG : CONTENT_EN;
  const t = bg
    ? { soon: "В развитие", title: "Специализирани под-модули" }
    : { soon: "Coming soon", title: "Specialised sub-modules" };

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="dhi-future-modules">
      {MODULES.map(({ key, icon: Icon, color }) => {
        const c = content[key];
        return (
          <div
            key={key}
            className={`${cardCls} p-5 relative overflow-hidden`}
            data-testid={`dhi-future-${key}`}
          >
            <div
              className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-10 pointer-events-none"
              style={{ background: color }}
            />
            <div className="flex items-start gap-3 mb-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: `${color}22`, color }}
              >
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base sm:text-lg font-bold">{c.title}</h3>
                  <span
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider"
                    style={{ background: `${color}22`, color }}
                  >
                    {t.soon}
                  </span>
                </div>
                <div className="text-[11px] opacity-60 mt-0.5">{c.scope}</div>
              </div>
            </div>

            <ul className="space-y-1.5 mb-3">
              {c.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-xs">
                  <span
                    className="mt-1.5 w-1 h-1 rounded-full shrink-0"
                    style={{ background: color }}
                  />
                  <span className="opacity-85 leading-snug">{b}</span>
                </li>
              ))}
            </ul>

            <p
              className={`text-[11px] italic leading-snug px-3 py-2 rounded-lg ${
                isDark ? "bg-white/[0.03]" : "bg-gray-50"
              }`}
              style={{ borderLeft: `3px solid ${color}` }}
            >
              {c.note}
            </p>
          </div>
        );
      })}
    </div>
  );
}
